/**
 * FEATURE: BATTLE_MODE
 * 
 * Dit bestand bevat alle Battle Mode functionaliteit.
 * 
 * Om deze feature terug te draaien:
 * 1. Zet FEATURE_FLAGS.BATTLE_MODE.enabled op false in featureFlags.js
 * 2. Alle Battle Mode code wordt dan overgeslagen
 * 
 * Voor volledige verwijdering:
 * - Zoek naar "FEATURE: BATTLE_MODE" in de hele codebase
 * - Verwijder alle gemarkeerde code blokken
 */

import { isBattleModeEnabled } from './featureFlags';
import Parse from '../parseClient';

/**
 * Check of Battle Mode enabled is voordat functionaliteit wordt uitgevoerd
 * @returns {boolean}
 */
function checkBattleModeEnabled() {
  if (!isBattleModeEnabled()) {
    console.log('[BattleMode] Feature is disabled via feature flag');
    return false;
  }
  return true;
}

// ============================================================================
// BATTLE MODE CLOUD FUNCTIONS
// ============================================================================

/**
 * Parse Cloud Function: startBattle
 * Start een nieuwe battle tussen twee gebruikers
 *
 * @param {string} opponentId - Parse objectId van de tegenstander
 * @param {"nl"|"en"} [language] - optioneel, battle-taal (default server: en)
 *
 * Om terug te draaien: verwijder deze functie en alle calls ernaar
 */
export async function startBattle(opponentId, language) {
  if (!checkBattleModeEnabled()) {
    throw new Error('Battle Mode is disabled');
  }

  try {
    const params = { opponentId };
    if (language === 'nl' || language === 'en') {
      params.language = language;
    }
    const result = await Parse.Cloud.run('startBattle', params);
    return result;
  } catch (error) {
    console.error('[BattleMode] Error starting battle:', error);
    throw error;
  }
}

/**
 * Parse Cloud Function: getBattleStatus
 * Haal de status van een battle op
 * 
 * Om terug te draaien: verwijder deze functie en alle calls ernaar
 */
export async function getBattleStatus(battleId) {
  if (!checkBattleModeEnabled()) {
    throw new Error('Battle Mode is disabled');
  }

  try {
    const result = await Parse.Cloud.run('getBattleStatus', {
      battleId: battleId,
    });
    return result;
  } catch (error) {
    console.error('[BattleMode] Error getting battle status:', error);
    throw error;
  }
}

/**
 * Parse Cloud Function: submitBattleScore
 * Submit een score voor een battle
 * 
 * Om terug te draaien: verwijder deze functie en alle calls ernaar
 */
export async function submitBattleScore(battleId, score) {
  if (!checkBattleModeEnabled()) {
    throw new Error('Battle Mode is disabled');
  }

  try {
    const result = await Parse.Cloud.run('submitBattleScore', {
      battleId: battleId,
      score: score,
    });
    return result;
  } catch (error) {
    console.error('[BattleMode] Error submitting battle score:', error);
    throw error;
  }
}

// ============================================================================
// BATTLE MODE UI HELPERS
// ============================================================================

/**
 * Check of de huidige gebruiker in een actieve battle zit
 * 
 * Om terug te draaien: verwijder deze functie en alle calls ernaar
 */
export async function hasActiveBattle() {
  if (!checkBattleModeEnabled()) {
    return false;
  }

  try {
    const result = await Parse.Cloud.run('hasActiveBattle');
    return result?.hasBattle === true;
  } catch (error) {
    console.error('[BattleMode] Error checking active battle:', error);
    return false;
  }
}

/**
 * Haal actieve battles op voor de huidige gebruiker
 * 
 * Om terug te draaien: verwijder deze functie en alle calls ernaar
 */
export async function getActiveBattles() {
  if (!checkBattleModeEnabled()) {
    return [];
  }

  try {
    const result = await Parse.Cloud.run('getActiveBattles');
    return result?.battles || [];
  } catch (error) {
    console.error('[BattleMode] Error getting active battles:', error);
    return [];
  }
}

// ============================================================================
// EXPORT CHECK
// ============================================================================

// Deze export wordt gebruikt om te checken of Battle Mode code geladen moet worden
export const BATTLE_MODE_AVAILABLE = isBattleModeEnabled();
